import * as Popper from "popper.js";
import * as React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toInputTime } from "../../utils/date-utils";
import './DateTimePicker.scss';
import { TimeZoneAbbreviations } from "../utility/timezones";

// #region Component Props

interface IProps {
    id                  : string;
    handleChange        : any;    
    dateValue?          : Date;

    disable?            : boolean;
    classes?            : string;
    showLabels?         : boolean;
    format?             : string;
    placeholder?        : string;
    onBlur?             : any;

    showTimezone?       : boolean;
    timezone?           : string;

    readOnly?           : boolean;
    highlightDates?     : Date[];

    maxDate?            : Date;
    minDate?            : Date;
    
    maxTime?            : Date;
    minTime?            : Date;
    
    includeDates?       : Date[];
    excludeDates?       : Date[];
    
    startDate?          : Date;
    endDate?            : Date;

    inline?             : boolean;
    withPortal?         : boolean;
    popperModifiers?    : Popper.Modifiers;
}

// #endregion

enum UserInputRenderProccess {
    None   = 0,
    Update = 1,
    Guard  = 2
}

const DateTimePicker : React.SFC<IProps> = (props) => {

    const { 
        id,
        classes,
        dateValue,
        showLabels, 
        format, 
        minDate,
        maxDate,
        disable,
        highlightDates,
        maxTime,
        minTime,
        includeDates,
        excludeDates,
        inline,
        popperModifiers,
        placeholder,
        onBlur,
        showTimezone,
        timezone
    } = props;

    const trueFormat = format ? format : 'MM/dd/yy';
    const passedInTimeProp = toInputTime(props.dateValue);
    const [localComponentTime, setLocalComponentTime] = React.useState(passedInTimeProp);
    const [fromUserInput, setFromUserInput] = React.useState(UserInputRenderProccess.None);

    /**
     * This component renders with every state change.
     * The prop that comes in remains largely the same, but
     * when it does change, we want to use that.  The rest of
     * the time we want the user's local changes to be the defacto
     * source of truth.  For our use case, we don't want to update
     * the parent state until the whole time element is set.
     * 
     * Some scenarios we've seen:
     * 1. Supply a new Date()
     * 2. Data loads from server
     * 3. Updated Date comes in 
     * Result: We want the updated prop to overwrite
     * the local time state we have
     * 
     * 
     * 1. Supply a new Date()
     * 2. Data loads from server
     * 3. Updated Date comes in
     * 4. Use the updated data
     * 5. User alters the time component
     * Result: onChange fires and component rerenders, we
     * have to track the the change is from the local input
     * not from the prop updating because in this case we
     * want the onChange event value to set the state
     * 
     * I don't love it, but this is where we are.
     */
    if (passedInTimeProp !== localComponentTime) {

        if (fromUserInput === UserInputRenderProccess.Update) {
            setLocalComponentTime(localComponentTime);
            setFromUserInput(UserInputRenderProccess.Guard);
        }
        else if (fromUserInput !== UserInputRenderProccess.Guard) {
            setLocalComponentTime(passedInTimeProp);
        }
        
    }

    /**
     * Split the time component provided by the input into hours
     * and minutes and update the date object with the new time
     * values
     */
    const updateTime = (e: any) => {
        
        if (e.target) {

            const inputVals = e.target.value.split(':');
            if (inputVals.length !== 2) {
                return;
            }

            const tmpDate = props.dateValue || new Date();            
            tmpDate.setHours(inputVals[0]);
            tmpDate.setMinutes(inputVals[1]);
            tmpDate.setSeconds(0);
            tmpDate.setMilliseconds(0);

            props.handleChange(tmpDate);

            if (props.onBlur) {
                props.onBlur();
            }
        }

    };

    /**
     * Handle updating the local time state
     * @param e form event objcet
     */
    const handleTimeChange = (e: any) => {
        setFromUserInput(UserInputRenderProccess.Update);
        setLocalComponentTime(e.target.value);
    };

    return <div className={"datetime-picker " + classes}>
            { showLabels ? <span>Date: </span> : null }
            <div className="datepicker-wrap">
                <DatePicker      
                    id={`${id}-date`}
                    todayButton={"Today"}
                    className="form-control "
                    autoComplete="off"
                    selected={ dateValue }                          
                    dateFormat={ trueFormat }
                    onChange={ props.handleChange }
                    disabled={ disable }
                    maxDate={ maxDate ? maxDate : undefined }
                    minDate={ minDate ? minDate : undefined }
                    highlightDates={ highlightDates }
                    maxTime={ maxTime }
                    placeholderText={ placeholder }
                    minTime={ minTime }
                    includeDates={ includeDates }
                    excludeDates={ excludeDates }
                    inline={ inline }
                    onBlur={ onBlur }
                    popperModifiers={ popperModifiers }
                />
            </div>
            { showLabels ? <span>Time: </span> : <span className="at-divider"> at </span> }
            <input 
                id={`${id}-time`}
                className="form-control" 
                type="time" 
                onBlur={ updateTime }
                value={ localComponentTime }
                disabled={ disable }
                onChange={ handleTimeChange } 
            />
            { showTimezone ? <span className="datepicker-tz">{ timezone ? TimeZoneAbbreviations[timezone] : 'UNK' }</span> : '' }
        </div>;
    
};

DateTimePicker.defaultProps = {
    classes         : '',
    showLabels      : false,
    format          : '',
    dateValue       : undefined,
    placeholder     : '',
    onBlur          : undefined,
    showTimezone    : false,
    timezone        : ''
};

export { DateTimePicker };
